import { IVenueBasket } from "@app-contexts/dashboard/features/box-office/basket/interfaces/IVenueBasket";
import type { ticketDeliveryTypes } from "@app-contexts/dashboard/features/box-office/checkout/hooks/checkout/useCheckout";
import { VenueBasedTicketTypeInventorySerializer } from "@app-contexts/dashboard/features/box-office/data/types/inventory";
import useVenueBasedTicketTypeInventoryService from "@app-contexts/dashboard/features/box-office/sell/data/services/inventory/useVenueBasedTicketTypeInventoryService";
import {
  getItemsGroupsByMixedShippingTypes,
  getItemsGroupsByShippingType,
  getMembershipItemsGroupsByShippingType,
  getProductsItemsGroupsByShippingType,
} from "@app-contexts/dashboard/features/box-office/utils/checkout/utils";
import { useVenueBasedMembershipGroupService } from "@app-contexts/dashboard/features/memberships/data/services/membership-group/useVenueBasedMembershipGroupService";
import { VenueBasedMembershipGroupSerializer } from "@app-contexts/dashboard/features/memberships/data/types/membership-groups";
import VenueBasedProductsRepository from "@app-contexts/dashboard/features/products/data/repositories/VenueBasedProductsRepository";
import useVenueProductsService from "@app-contexts/dashboard/features/products/data/services/useVenueProductsService";
import { VenueBasedProductSerializer } from "@app-contexts/dashboard/features/products/data/types";
import { UserBasedVenueSerializer } from "@app-contexts/dashboard/features/venues/data/types/user-venue";
import { UsePublicBasketType } from "@app-contexts/public/features/basket/services/usePublicBasket";
import usePublicEventsService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import { PublicVenueSerializer } from "@app-contexts/public/features/org-profile/data/types/public-venue";
import PublicBasedProductsRepository from "@app-contexts/public/features/products/data/repositories/PublicBasedProductsRepository";
import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import { DELIVERY_TYPE_OPTIONS, enums } from "@shared/constants";
import { DataProvider } from "@shared/constants/common";
import { ShippingMethodsTypeMapIdType } from "@shared/constants/enums/enums-types";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import usePublicMembershipGroupsService from "@shared/modules/memberships/data/services/usePublicMembershipGroupsService";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import { BaseVenueSerializer } from "@shared/modules/venue/types";
import { QueryKeyManager } from "@shared/services/api/hooks/utils/queryKeyManager";
import { PaginatorContract } from "@shared/services/api/types";
import { getFlattenedData } from "@shared/utils/data/data-utils";
import { recordAppError } from "@shared/utils/errors/errorLogger";
import { isStorageAvailable } from "@shared/utils/storage/cache-storage";
import { uniq } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { InfiniteData, useQueries, useQueryClient } from "react-query";

/** The shipping type "st_print" is displayed in the UI as Electronic ticket and
 * has "Email" and "Print" buttons, which correspond to the ticketDeliveryTypes "email" and "print".
 * */
export type DeliveryMethodType =
  | ShippingMethodsTypeMapIdType
  | (typeof ticketDeliveryTypes)[number]["id"];
export type SelectedDeliveryMethod = Record<string, DeliveryMethodType>;

const useCheckoutDeliveryMethods = ({
  venue,
  basket,
  //
  useTicketTypeService,
  useEventService,
  useMembershipService,
  onSetDeliveryMethod,
  productServiceType = DataProvider.Venue,
}: {
  productServiceType?: DataProvider;
  venue:
    | BaseVenueSerializer
    | UserBasedVenueSerializer
    | PublicVenueSerializer
    | undefined;
  basket: IVenueBasket | UsePublicBasketType;
  //
  useTicketTypeService?: typeof useVenueBasedTicketTypeInventoryService;
  useEventService?: typeof usePublicEventsService;
  useMembershipService?:
    | typeof useVenueBasedMembershipGroupService
    | typeof usePublicMembershipGroupsService;
  onSetDeliveryMethod?: (
    method: DeliveryMethodType,
    selectedMethods: SelectedDeliveryMethod,
    mixedShippingTypeItemGroups: Array<{
      shippingTypes: ShippingMethodsTypeMapIdType[];
      itemGroups: TicketItemGroupType[];
    }>
  ) => void;
}) => {
  const currentVenue = venue;
  const { itemGroups, modifyItemGroups, isBasketUpdating } = basket;
  const queryClient = useQueryClient();
  // Products on public pages need to be fetched individually to ensure the specified products are returned
  // This is because the public products paginated endpoint respects the is_public flag and may not return all products
  const isPublicProductService = productServiceType === DataProvider.Public;

  // Get event ids from basket items
  const eventIds = useMemo(() => {
    if (!itemGroups?.length) return [];
    const events = itemGroups.filter(
      (group) => group.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id
    );

    return uniq(events.map((item) => item.event as number));
  }, [itemGroups]);

  // Get uniq ticket ids from basket items
  const ticketTypeIds = useMemo(() => {
    if (!itemGroups?.length) return [];
    const tickets = itemGroups.filter(
      (group) => group.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id
    );

    return uniq(tickets.map((item) => item.type));
  }, [itemGroups]);

  // Get uniq product ids from basket items
  const productIds = useMemo(() => {
    if (!itemGroups?.length) return [];
    const products = itemGroups.filter(
      (group) => group.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id
    );

    return uniq(products.map((item) => item.product));
  }, [itemGroups]);

  // Get uniq membership level ids from basket items
  const membershipLevelIds = useMemo(() => {
    if (!itemGroups?.length) return [];
    const memberships = itemGroups.filter(
      (group) =>
        group.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
    );

    // Use the correct property name: membership_level
    return uniq(memberships.map((item) => item.membership_group));
  }, [itemGroups]);

  const ttService = useTicketTypeService?.(currentVenue?.id);
  const eventsService = useEventService?.();

  const productService = useVenueProductsService({
    options: {
      enabled: !isPublicProductService,
    },
  });
  const membershipService = useMembershipService?.();

  const { data: ttServiceTicketTypes, isLoading: isTicketTypesLoading } =
    ttService
      ? ttService.usePaginated({
          params: {
            page_size: ticketTypeIds.length,
            ticket_type__in: ticketTypeIds.join(","),
          },
          options: {
            enabled: Boolean(currentVenue?.id && ticketTypeIds.length),
          },
        })
      : { data: null, isLoading: null };

  const { data: eventsServiceEvents, isLoading: isEventsServiceEventsLoading } =
    eventsService
      ? eventsService.usePaginated({
          params: {
            page_size: eventIds.length,
            id__in: eventIds.join(","),
            show: "all",
          },
        })
      : { data: null, isLoading: false };

  const { data: memberships, isLoading: isMembershipsLoading } =
    membershipService
      ? membershipService.usePaginated({
          params: {
            page_size: membershipLevelIds.length,
            id__in: membershipLevelIds.join(","),
            show: "all",
          },
          options: {
            enabled: Boolean(currentVenue?.id && membershipLevelIds.length),
          },
        })
      : { data: null, isLoading: false };

  const ticketTypeItems = useMemo(() => {
    if (ttServiceTicketTypes) {
      return getFlattenedData<VenueBasedTicketTypeInventorySerializer>(
        ttServiceTicketTypes
      );
    }

    if (eventsServiceEvents) {
      const ticketTypes: Exclude<
        PublicBaseEventSerializer["ticket_types"],
        undefined
      > = [];

      const exchangeCredit = isStorageAvailable("localStorage")
        ? localStorage.getItem("exchangeCreditData")
        : null;
      const exchangeCreditData = JSON.parse(exchangeCredit || "{}");

      eventsServiceEvents.pages.forEach((page) => {
        page.results.forEach((event) => {
          if (event.ticket_types) {
            ticketTypes.push(...event.ticket_types);
          }
          // If the user has exchange credits and the event has membership_ticket_types, add them to the ticketTypes array
          if (
            exchangeCreditData.is_membership_ticket_exchange &&
            event.membership_ticket_types
          ) {
            ticketTypes.push(...event.membership_ticket_types);
          }
        });
      });

      return ticketTypes;
    }

    return [];
  }, [ttServiceTicketTypes, eventsServiceEvents]);

  const venueProductRepo = useMemo(
    () => new VenueBasedProductsRepository({ venueId: currentVenue?.id }),
    [currentVenue?.id]
  );

  const publicProductRepo = useMemo(
    () => new PublicBasedProductsRepository(),
    []
  );

  // TODO: update the cache query key here with useModelService when we update the queries on the sell page
  // get all cache for products queries.
  // these should have been queried when a user is on the sell page
  const venueProductsCache = queryClient.getQueriesData<
    InfiniteData<PaginatorContract<VenueBasedProductSerializer>>
  >([venueProductRepo.buildUrl()]);

  const flattenedProductsCache = useMemo(() => {
    if (!venueProductsCache) {
      return [];
    }
    const current: Array<Array<VenueBasedProductSerializer>> = [];

    venueProductsCache.forEach((data) => {
      if (data[1]) {
        current.push(getFlattenedData(data[1]));
      }
    });
    return current
      .flat()
      .filter(
        (item, index, self) => index === self.findIndex((t) => t.id === item.id)
      );
  }, [venueProductsCache]);

  const queryKeyManager = useMemo(
    () => new QueryKeyManager(publicProductRepo),
    [publicProductRepo]
  );

  const productQueries = useQueries(
    productIds.map((id) => ({
      queryKey: queryKeyManager.generateKey(["retrieve", id.toString()]),
      queryFn: () => publicProductRepo.retrieve(id),
      enabled: Boolean(
        currentVenue?.id &&
          id &&
          !flattenedProductsCache.length &&
          isPublicProductService
      ),
      keepPreviousData: true,
      staleTime: 2.5 * 60 * 1000, // 2.5 minutes; quarter-life of a 10 minute checkout time limit
    }))
  );

  // Extract data and loading state from productQueries
  const publicProducts = useMemo(() => {
    if (
      !isPublicProductService ||
      !productQueries.length ||
      flattenedProductsCache.length
    )
      return [];
    return productQueries
      .filter((query) => query.isSuccess && query.data !== undefined)
      .map((query) => query.data as ProductTypeSerializer);
  }, [productQueries, isPublicProductService, flattenedProductsCache.length]);

  const isPublicProductsLoading = useMemo(() => {
    return (
      isPublicProductService && productQueries.some((query) => query.isLoading)
    );
  }, [productQueries, isPublicProductService]);

  const { data: venueProducts, isLoading: isVenueProductsLoading } =
    productService.usePaginated({
      params: {
        page_size: productIds.length,
        id__in: productIds.join(","),
      },
      options: {
        // dont query, if products already loaded in cache
        // if nothing in cache, this will fetch for just the products in the cart
        // TODO shouldn't have to do this as the entire premise of React Query is to reduce network calls
        // by relying on the same query keys.
        enabled: Boolean(
          currentVenue?.id &&
            productIds.length &&
            !flattenedProductsCache.length &&
            !isPublicProductService
        ),
      },
    });

  const isProductsLoading = isVenueProductsLoading || isPublicProductsLoading;
  const isEventsLoading = isEventsServiceEventsLoading;

  const productItems = useMemo(() => {
    if (flattenedProductsCache.length) {
      return flattenedProductsCache;
    }

    if (publicProducts && publicProducts.length) {
      return publicProducts as
        | VenueBasedProductSerializer[]
        | ProductTypeSerializer[];
    }

    if (venueProducts?.pages.length) {
      return getFlattenedData<
        VenueBasedProductSerializer | ProductTypeSerializer
      >(venueProducts);
    }

    return [];
  }, [venueProducts, flattenedProductsCache, publicProducts]);

  const membershipItems = useMemo(() => {
    if (memberships?.pages.length) {
      return getFlattenedData<
        VenueBasedMembershipGroupSerializer | PublicMembershipGroupSerializer
      >(memberships);
    }

    return [];
  }, [memberships]);

  // tickets, products, and memberships in the basket with shipping_type: st_print only,
  // showed in the first card as "Electronic tickets" with Email and Print buttons
  const shippingTypePrintItemGroups = useMemo(() => {
    let tickets: TicketItemGroupType[] = [];
    let products: TicketItemGroupType[] = [];
    let memberships: TicketItemGroupType[] = [];

    if (ticketTypeItems.length && itemGroups.length) {
      tickets = getItemsGroupsByShippingType(
        ticketTypeItems,
        itemGroups,
        enums.SHIPPING_METHODS.PRINT.id
      );
    }

    if (productItems.length && itemGroups.length) {
      products = getProductsItemsGroupsByShippingType(
        productItems,
        itemGroups,
        enums.SHIPPING_METHODS.PRINT.id
      );
    }

    if (membershipItems.length && itemGroups.length) {
      memberships = getMembershipItemsGroupsByShippingType(
        membershipItems,
        itemGroups,
        enums.SHIPPING_METHODS.PRINT.id
      );
    }

    return [...tickets, ...products, ...memberships];
  }, [ticketTypeItems, productItems, membershipItems, itemGroups]);

  // tickets, products, and memberships with shipping_type: st_will_call only
  // showed as "Will call" in UI
  const shippingTypeWillCallItemGroups = useMemo(() => {
    let tickets: TicketItemGroupType[] = [];
    let products: TicketItemGroupType[] = [];
    let memberships: TicketItemGroupType[] = [];

    if (ticketTypeItems.length && itemGroups.length) {
      tickets = getItemsGroupsByShippingType(
        ticketTypeItems,
        itemGroups,
        enums.SHIPPING_METHODS.WILL_CALL.id
      );
    }

    if (productItems.length && itemGroups.length) {
      products = getProductsItemsGroupsByShippingType(
        productItems,
        itemGroups,
        enums.SHIPPING_METHODS.WILL_CALL.id
      );
    }

    if (membershipItems.length && itemGroups.length) {
      memberships = getMembershipItemsGroupsByShippingType(
        membershipItems,
        itemGroups,
        enums.SHIPPING_METHODS.WILL_CALL.id
      );
    }

    return [...tickets, ...products, ...memberships];
  }, [ticketTypeItems, productItems, membershipItems, itemGroups]);

  // tickets, products, and memberships with shipping_type: st_delivery only
  // showed as "Standard shipping" in UI
  const shippingTypeDeliveryItemGroups = useMemo(() => {
    let tickets: TicketItemGroupType[] = [];
    let products: TicketItemGroupType[] = [];
    let memberships: TicketItemGroupType[] = [];

    if (ticketTypeItems.length && itemGroups.length) {
      tickets = getItemsGroupsByShippingType(
        ticketTypeItems,
        itemGroups,
        enums.SHIPPING_METHODS.DELIVERY.id
      );
    }

    if (productItems.length && itemGroups.length) {
      products = getProductsItemsGroupsByShippingType(
        productItems,
        itemGroups,
        enums.SHIPPING_METHODS.DELIVERY.id
      );
    }

    if (membershipItems.length && itemGroups.length) {
      memberships = getMembershipItemsGroupsByShippingType(
        membershipItems,
        itemGroups,
        enums.SHIPPING_METHODS.DELIVERY.id
      );
    }

    return [...tickets, ...products, ...memberships];
  }, [ticketTypeItems, productItems, membershipItems, itemGroups]);

  // tickets, products, and memberships if shipping types includes more then one shipping type
  // for Ex.: [st_print, st_delivery]
  const mixedShippingTypesItemGroups = useMemo(() => {
    if (itemGroups.length) {
      return getItemsGroupsByMixedShippingTypes(
        ticketTypeItems,
        productItems,
        membershipItems,
        itemGroups
      );
    }

    return [];
  }, [ticketTypeItems, productItems, membershipItems, itemGroups]);

  const isShippingAddressRequired = useMemo(() => {
    return itemGroups.some(
      (group) => group.shipping_type === enums.SHIPPING_METHODS.DELIVERY.id
    );
  }, [itemGroups]);

  const [selectedDeliveryMethod, setSelectedDeliveryMethod] =
    useState<SelectedDeliveryMethod>({});

  // this method used for an activation tab and calls update callback if delivery method changed
  // isEticket true if method called from card that includes only shipping_type: st_print items
  const setDeliveryMethod = useCallback(
    async (method: DeliveryMethodType, isEticket = false) => {
      const itemGroupsForUpdate: TicketItemGroupType[] = [];
      const selectedMethods = { ...selectedDeliveryMethod };
      let normalizedMethod = method as ShippingMethodsTypeMapIdType;

      // st_print gives two tabs in UI: print and email
      if (
        method === DELIVERY_TYPE_OPTIONS.TYPE_PRINT.id ||
        method === DELIVERY_TYPE_OPTIONS.TYPE_EMAIL.id
      ) {
        normalizedMethod = enums.SHIPPING_METHODS.PRINT.id;
      }

      // for items that have more than one shipping_type method set tabs with selected method
      mixedShippingTypesItemGroups.forEach((group) => {
        const tabs = group.shippingTypes.toString();

        if (
          // if called from a card with shipping_type: [st_print] items only
          // and group shipping_type already st_print
          (isEticket &&
            group.itemGroups[0].shipping_type ===
              enums.SHIPPING_METHODS.PRINT.id) ||
          // if called from a card with shipping_type that includes more than one type
          // and group includes selected method
          (!isEticket && group.shippingTypes.includes(normalizedMethod))
        ) {
          // set active tabs
          setSelectedDeliveryMethod((prev) => ({
            ...prev,
            [tabs]: method,
          }));
          selectedMethods[tabs] = method;
        }

        if (!isEticket && group.shippingTypes.includes(normalizedMethod)) {
          // save itemGroups that need to be updated
          itemGroupsForUpdate.push(...group.itemGroups);
        }
      });

      try {
        onSetDeliveryMethod?.(
          method,
          selectedMethods,
          mixedShippingTypesItemGroups
        );
      } catch (error) {
        recordAppError(error);
      }

      // update itemGroups
      if (itemGroupsForUpdate.length) {
        const newItemGroups = itemGroupsForUpdate.map((item) => ({
          itemGroupId: item.id,
          fieldsToUpdate: { shipping_type: normalizedMethod },
        }));

        try {
          await modifyItemGroups(newItemGroups, {
            shouldPersist: true,
          });
        } catch (error) {
          recordAppError(error);
          return undefined;
        }
      }

      return undefined;
    },
    [
      selectedDeliveryMethod,
      setSelectedDeliveryMethod,
      mixedShippingTypesItemGroups,
      modifyItemGroups,
    ]
  );

  const hasDeliveryInMixedShippingTypes = useMemo(() => {
    return mixedShippingTypesItemGroups.some((group) =>
      group.shippingTypes.includes(enums.SHIPPING_METHODS.DELIVERY.id)
    );
  }, [mixedShippingTypesItemGroups]);

  const shippingIsAllowed = useMemo(
    () =>
      hasDeliveryInMixedShippingTypes ||
      shippingTypeDeliveryItemGroups.length > 0,
    [hasDeliveryInMixedShippingTypes, shippingTypeDeliveryItemGroups]
  );

  return {
    isLoading:
      isTicketTypesLoading ||
      isEventsLoading ||
      isProductsLoading ||
      isMembershipsLoading,
    // ticket itemGroups
    shippingTypePrintItemGroups,
    shippingTypeWillCallItemGroups,
    shippingTypeDeliveryItemGroups,
    mixedShippingTypesItemGroups,
    // form
    isShippingAddressRequired,
    // tabs
    selectedDeliveryMethod,
    setDeliveryMethod,
    isDeliveryMethodUpdating: isBasketUpdating,
    shippingIsAllowed,
  };
};

export default useCheckoutDeliveryMethods;
